import { type RouteKey } from '@/lib/routes';

export type LocalizedMeta = {
  en: { title: string; description: string; keywords: string };
  es: { title: string; description: string; keywords: string };
};

export const seoMetaMap: Record<Exclude<RouteKey, 'blogPost'>, LocalizedMeta> = {
  home: {
    en: {
      title: 'PriceZing | Pricing Strategy Consulting & AI Platform',
      description: 'Expert pricing strategy consulting, AI monitoring, and certified market research to grow margins across LATAM & U.S.',
      keywords: 'pricing strategy, AI pricing, price optimization, market research, LATAM pricing'
    },
    es: {
      title: 'PriceZing | Consultoría de Precios y Plataforma IA',
      description: 'Consultoría experta en precios, monitoreo con IA e investigación certificada para crecer márgenes en LATAM y EE.UU.',
      keywords: 'estrategia de precios, precios IA, optimización de precios, investigación de mercado, LATAM'
    }
  },
  about: {
    en: {
      title: 'About PriceZing | 18+ Years in Pricing Strategy',
      description: 'Meet our pricing experts led by Lina Restrepo. 500+ projects across LATAM delivering defendable, value-based pricing.',
      keywords: 'about, pricing experts, Lina Restrepo, value-based pricing, LATAM'
    },
    es: {
      title: 'Acerca de PriceZing | 18+ Años en Estrategia de Precios',
      description: 'Conoce a nuestros expertos liderados por Lina Restrepo. 500+ proyectos en LATAM con precios defendibles basados en valor.',
      keywords: 'acerca, expertos en precios, Lina Restrepo, precios basados en valor, LATAM'
    }
  },
  services: {
    en: {
      title: 'Pricing Services | Monitoring, Consulting & Research',
      description: 'AI-powered monitoring, pricing strategy consulting, and certified international research to transform pricing decisions.',
      keywords: 'pricing services, consulting, monitoring, international research'
    },
    es: {
      title: 'Servicios de Precios | Monitoreo, Consultoría e Investigación',
      description: 'Monitoreo con IA, consultoría de estrategia de precios e investigación internacional certificada para transformar decisiones.',
      keywords: 'servicios de precios, consultoría, monitoreo, investigación internacional'
    }
  },
  smartPricing: {
    en: {
      title: 'Smart Pricing Platform | AI Price Optimization',
      description: 'Real-time competitor monitoring, AI price recommendations, and automated rules to boost margins with confidence.',
      keywords: 'smart pricing platform, AI pricing, competitor monitoring, automation'
    },
    es: {
      title: 'Plataforma Smart Pricing | Optimización de Precios con IA',
      description: 'Monitoreo de competidores en tiempo real, recomendaciones de precios con IA y reglas automatizadas para mejorar márgenes.',
      keywords: 'plataforma pricing, precios IA, monitoreo competidores, automatización'
    }
  },
  consulting: {
    en: {
      title: 'Pricing Strategy Consulting | Expert Advisors',
      description: 'From diagnostic to execution: pricing models, positioning, elasticity and omnichannel strategies for profitable growth.',
      keywords: 'pricing consulting, pricing models, elasticity, omnichannel, strategy'
    },
    es: {
      title: 'Consultoría en Estrategia de Precios | Expertos',
      description: 'Del diagnóstico a la ejecución: modelos de precios, posicionamiento, elasticidad y estrategia omnicanal para crecer rentable.',
      keywords: 'consultoría de precios, modelos de precios, elasticidad, omnicanal, estrategia'
    }
  },
  research: {
    en: {
      title: 'International Market Research | Certified Pricing',
      description: 'Certified pricing studies for market entry, expansion and trade defense. Audit-ready, regulator-friendly reports.',
      keywords: 'international research, pricing studies, market entry, trade defense'
    },
    es: {
      title: 'Investigación de Mercado Internacional | Precios Certificados',
      description: 'Estudios certificados de precios para entrada, expansión y defensa comercial. Reportes auditables y defendibles.',
      keywords: 'investigación internacional, estudios de precios, entrada mercado, defensa comercial'
    }
  },
  blog: {
    en: {
      title: 'Pricing Blog | Insights & Strategies by PriceZing',
      description: 'Articles on pricing strategy, market analysis, smart pricing and international research to improve profitability.',
      keywords: 'pricing blog, pricing insights, market analysis, smart pricing'
    },
    es: {
      title: 'Blog de Precios | Insights y Estrategias de PriceZing',
      description: 'Artículos sobre estrategia de precios, análisis de mercado, smart pricing e investigación internacional para mejorar rentabilidad.',
      keywords: 'blog de precios, insights de precios, análisis de mercado, smart pricing'
    }
  },
  contact: {
    en: {
      title: 'Contact PriceZing | Book a Free Consultation',
      description: 'Talk to our pricing experts. Book a free diagnostic session and discover margin and revenue opportunities.',
      keywords: 'contact, pricing consultation, free diagnostic, pricing experts'
    },
    es: {
      title: 'Contacta PriceZing | Agenda una Consulta Gratuita',
      description: 'Habla con nuestros expertos en precios. Agenda un diagnóstico gratuito y descubre oportunidades de margen e ingresos.',
      keywords: 'contacto, consulta de precios, diagnóstico gratuito, expertos en precios'
    }
  },
  privacyPolicy: {
    en: {
      title: 'Privacy Policy | PriceZing Solutions',
      description: 'How we protect and handle personal data across our consulting and platform services.',
      keywords: 'privacy policy, data protection, GDPR'
    },
    es: {
      title: 'Política de Privacidad | PriceZing Solutions',
      description: 'Cómo protegemos y gestionamos los datos personales en nuestra consultoría y plataforma.',
      keywords: 'política de privacidad, protección de datos, RGPD'
    }
  },
  termsOfService: {
    en: {
      title: 'Terms of Service | PriceZing Solutions',
      description: 'Service terms, acceptable use, responsibilities and limitations for using PriceZing services.',
      keywords: 'terms of service, legal, acceptable use'
    },
    es: {
      title: 'Términos de Servicio | PriceZing Solutions',
      description: 'Términos del servicio, uso aceptable, responsabilidades y limitaciones para usar PriceZing.',
      keywords: 'términos de servicio, legal, uso aceptable'
    }
  },
  cookiePolicy: {
    en: {
      title: 'Cookie Policy | PriceZing Solutions',
      description: 'What cookies we use, why we use them and how you can manage preferences.',
      keywords: 'cookie policy, cookies, tracking, preferences'
    },
    es: {
      title: 'Política de Cookies | PriceZing Solutions',
      description: 'Qué cookies usamos, para qué las usamos y cómo gestionar preferencias.',
      keywords: 'política de cookies, cookies, seguimiento, preferencias'
    }
  },
  disclaimer: {
    en: {
      title: 'Legal Disclaimer | PriceZing Solutions',
      description: 'Important information and limitations about our website and consulting services.',
      keywords: 'disclaimer, legal, liability'
    },
    es: {
      title: 'Descargo de Responsabilidad | PriceZing Solutions',
      description: 'Información importante y limitaciones sobre nuestro sitio y servicios de consultoría.',
      keywords: 'descargo, legal, responsabilidad'
    }
  },
  dataProcessing: {
    en: {
      title: 'Data Processing (GDPR) | PriceZing Solutions',
      description: 'Roles, safeguards and measures for GDPR-compliant data processing.',
      keywords: 'data processing, GDPR, compliance'
    },
    es: {
      title: 'Procesamiento de Datos (RGPD) | PriceZing Solutions',
      description: 'Roles, salvaguardas y medidas para procesamiento de datos conforme al RGPD.',
      keywords: 'procesamiento de datos, RGPD, cumplimiento'
    }
  }
};
